import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Send, 
  MessageSquare, 
  Phone, 
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { getAllUsers, createNotification, getRecentNotifications, sendSMS, sendWhatsApp, sendEmail } from '../services/api';
import { User as UserType, Notification as NotificationData } from '../types';

const NotificationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

// Removed unused Input styled component

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

// Removed unused CheckboxGroup styled component

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #667eea;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'whatsapp' | 'sms' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'whatsapp':
        return `
          background: #25d366;
          color: white;
          
          &:hover {
            background: #1da851;
          }
        `;
      case 'sms':
        return `
          background: #007bff;
          color: white;
          
          &:hover {
            background: #0056b3;
          }
        `;
      default:
        return `
          background: #f8f9fa;
          color: #333;
          border: 2px solid #e1e5e9;
          
          &:hover {
            background: #e9ecef;
          }
        `;
    }
  }}
`;

const NotificationList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const NotificationItem = styled.div<{ status: string }>`
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 10px;
  border-right: 4px solid ${props => 
    props.status === 'sent' ? '#28a745' : 
    props.status === 'failed' ? '#dc3545' : '#ffc107'
  };
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const NotificationType = styled.div<{ type: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.type === 'whatsapp' ? '#25d366' : '#007bff'};
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: #666;
`;

const NotificationMessage = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
`;

const NotificationRecipient = styled.div`
  font-size: 12px;
  color: #666;
`;

const StatusIcon = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => 
    props.status === 'sent' ? '#28a745' : 
    props.status === 'failed' ? '#dc3545' : '#ffc107'
  };
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-style: italic;
`;

const Notifications: React.FC = () => {
  const [members, setMembers] = useState<UserType[]>([]);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [formData, setFormData] = useState({
    message: '',
    recipients: [] as string[],
    notificationType: 'whatsapp' as 'whatsapp' | 'sms' | 'email',
    sendToAll: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [membersData, notificationsData] = await Promise.all([
        getAllUsers(),
        getRecentNotifications(50)
      ]);
      
      setMembers(membersData);
      setNotifications(notificationsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleRecipientChange = (memberId: string, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        recipients: [...formData.recipients, memberId]
      });
    } else {
      setFormData({
        ...formData,
        recipients: formData.recipients.filter(id => id !== memberId)
      });
    }
  };

  const handleSendToAllChange = (checked: boolean) => {
    // Get eligible members based on notification type
    const eligibleMembers = members.filter(member => {
      if (formData.notificationType === 'whatsapp') {
        return member.notificationPreferences.whatsapp;
      } else if (formData.notificationType === 'sms') {
        return member.notificationPreferences.sms;
      } else if (formData.notificationType === 'email') {
        return member.notificationPreferences.email && member.email;
      }
      return true;
    });

    setFormData({
      ...formData,
      sendToAll: checked,
      recipients: checked ? eligibleMembers.map(m => m.id) : []
    });
  };

  const handleSendNotification = async () => {
    if (!formData.message.trim()) {
      alert('אנא הכנס הודעה');
      return;
    }

    if (!formData.sendToAll && formData.recipients.length === 0) {
      alert('אנא בחר נמענים');
      return;
    }

    setSending(true);

    try {
      // Get eligible members based on notification type
      const eligibleMembers = members.filter(member => {
        if (formData.notificationType === 'whatsapp') {
          return member.notificationPreferences.whatsapp;
        } else if (formData.notificationType === 'sms') {
          return member.notificationPreferences.sms;
        } else if (formData.notificationType === 'email') {
          return member.notificationPreferences.email && member.email;
        }
        return true;
      });

      const recipients = formData.sendToAll ? eligibleMembers : eligibleMembers.filter(m => formData.recipients.includes(m.id));

      const promises = recipients.map(async (member) => {
        let success = false;
        
        if (formData.notificationType === 'whatsapp') {
          success = await sendWhatsApp(member.phone, formData.message);
        } else if (formData.notificationType === 'sms') {
          success = await sendSMS(member.phone, formData.message);
        } else if (formData.notificationType === 'email') {
          success = await sendEmail(member.email!, 'הודעה מבית הכנסת זכרון דליה', formData.message);
        }

        // Create notification record
        await createNotification({
          recipientId: member.id,
          recipientPhone: member.phone,
          message: formData.message,
          type: formData.notificationType,
          status: success ? 'sent' : 'failed',
          sentAt: success ? new Date() : undefined
        });

        return { member, success };
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      alert(`הודעות נשלחו: ${successCount} הצליחו, ${failCount} נכשלו`);

      // Reset form
      setFormData({
        message: '',
        recipients: [],
        notificationType: 'whatsapp',
        sendToAll: false
      });

      // Reload notifications
      await loadData();
    } catch (error) {
      console.error('Error sending notifications:', error);
      alert('שגיאה בשליחת ההודעות');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <NotificationsContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </NotificationsContainer>
    );
  }

  return (
    <NotificationsContainer>
      <Header>
        <Title>
          <MessageSquare size={28} />
          שליחת הודעות
        </Title>
      </Header>

      <ContentGrid>
        <Section>
          <SectionTitle>
            <Send size={20} />
            שליחת הודעה חדשה
          </SectionTitle>

          <FormGroup>
            <Label>סוג הודעה</Label>
            <Select
              value={formData.notificationType}
              onChange={(e) => setFormData({ ...formData, notificationType: e.target.value as any })}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
              <option value="email">אימייל</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>הודעה *</Label>
            <TextArea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="הכנס את תוכן ההודעה כאן..."
              maxLength={formData.notificationType === 'sms' ? 160 : 1000}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {formData.message.length} / {formData.notificationType === 'sms' ? 160 : 1000} תווים
            </div>
            <div style={{ fontSize: '12px', color: '#667eea', marginTop: '5px', fontWeight: '600' }}>
              {(() => {
                const eligibleCount = members.filter(member => {
                  if (formData.notificationType === 'whatsapp') {
                    return member.notificationPreferences.whatsapp;
                  } else if (formData.notificationType === 'sms') {
                    return member.notificationPreferences.sms;
                  } else if (formData.notificationType === 'email') {
                    return member.notificationPreferences.email && member.email;
                  }
                  return true;
                }).length;
                
                const typeText = formData.notificationType === 'whatsapp' ? 'WhatsApp' : 
                                formData.notificationType === 'sms' ? 'SMS' : 'אימייל';
                
                return `${eligibleCount} מתפללים זמינים ל${typeText}`;
              })()}
            </div>
          </FormGroup>

          <FormGroup>
            <CheckboxItem>
              <Checkbox
                type="checkbox"
                checked={formData.sendToAll}
                onChange={(e) => handleSendToAllChange(e.target.checked)}
              />
              שלח לכל המתפללים
            </CheckboxItem>
          </FormGroup>

          {!formData.sendToAll && (
            <FormGroup>
              <Label>בחר נמענים</Label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e1e5e9', borderRadius: '8px', padding: '10px' }}>
                {members
                  .filter(member => {
                    // Filter members based on notification type
                    if (formData.notificationType === 'whatsapp') {
                      return member.notificationPreferences.whatsapp;
                    } else if (formData.notificationType === 'sms') {
                      return member.notificationPreferences.sms;
                    } else if (formData.notificationType === 'email') {
                      return member.notificationPreferences.email && member.email;
                    }
                    return true;
                  })
                  .map(member => (
                    <CheckboxItem key={member.id} style={{ marginBottom: '8px' }}>
                      <Checkbox
                        type="checkbox"
                        checked={formData.recipients.includes(member.id)}
                        onChange={(e) => handleRecipientChange(member.id, e.target.checked)}
                      />
                      {member.name} {member.lastName} - {member.phone}
                      {member.email && formData.notificationType === 'email' && (
                        <span style={{ color: '#666', fontSize: '12px' }}> ({member.email})</span>
                      )}
                    </CheckboxItem>
                  ))}
              </div>
            </FormGroup>
          )}

          <ButtonGroup>
            <Button
              variant={formData.notificationType === 'whatsapp' ? 'whatsapp' : 'sms'}
              onClick={handleSendNotification}
              disabled={sending}
            >
              {sending ? 'שולח...' : 'שלח הודעה'}
            </Button>
          </ButtonGroup>
        </Section>

        <Section>
          <SectionTitle>
            <Clock size={20} />
            הודעות אחרונות
          </SectionTitle>

          <NotificationList>
            {notifications.length === 0 ? (
              <EmptyState>
                אין הודעות אחרונות
              </EmptyState>
            ) : (
              notifications.map((notification) => (
                <NotificationItem key={notification.id} status={notification.status}>
                  <NotificationHeader>
                    <NotificationType type={notification.type}>
                      {notification.type === 'whatsapp' ? (
                        <MessageSquare size={14} />
                      ) : (
                        <Phone size={14} />
                      )}
                      {notification.type === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                    </NotificationType>
                    <NotificationTime>
                      {(() => {
                        try {
                          let date;
                          if (notification.createdAt && typeof notification.createdAt === 'object' && 'toDate' in notification.createdAt) {
                            date = notification.createdAt.toDate();
                          } else if (notification.createdAt instanceof Date) {
                            date = notification.createdAt;
                          } else {
                            date = new Date(notification.createdAt);
                          }
                          return date.toLocaleString('he-IL');
                        } catch (error) {
                          console.warn('Error displaying notification date:', error);
                          return 'תאריך לא תקין';
                        }
                      })()}
                    </NotificationTime>
                  </NotificationHeader>
                  
                  <NotificationMessage>
                    {notification.message}
                  </NotificationMessage>
                  
                  <NotificationRecipient>
                    {notification.recipientPhone}
                  </NotificationRecipient>
                  
                  <StatusIcon status={notification.status}>
                    {notification.status === 'sent' ? (
                      <>
                        <CheckCircle size={12} />
                        נשלח
                      </>
                    ) : notification.status === 'failed' ? (
                      <>
                        <XCircle size={12} />
                        נכשל
                      </>
                    ) : (
                      <>
                        <Clock size={12} />
                        ממתין
                      </>
                    )}
                  </StatusIcon>
                </NotificationItem>
              ))
            )}
          </NotificationList>
        </Section>
      </ContentGrid>
    </NotificationsContainer>
  );
};

export default Notifications;
